import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import { Star, Bell, BellOff, Phone, PhoneOff, Mail, MailX, Eye, EyeOff, GripVertical, AtSign } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';
import { Room } from '../../models/Room';
import { RoomSetting } from '../../models/User';
import { My } from '../../services/My';

interface RoomManagerModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rooms: Room[];
    oneToOneRooms: Room[];
    onNavigateToRoom: (roomId: string) => void;
}

type TabType = 'rooms' | 'oneToOne';

const RoomManagerModal: React.FC<RoomManagerModalProps> = ({
    open, onOpenChange, rooms, oneToOneRooms, onNavigateToRoom,
}) => {
    const [activeTab, setActiveTab] = useState<TabType>('rooms');

    // Touch drag state
    const [dragId, setDragId] = useState<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    const dragRef = useRef<string | null>(null);
    const touchStartY = useRef<number>(0);
    const listRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    const currentRooms = activeTab === 'rooms' ? rooms : oneToOneRooms;
    const currentOrder = activeTab === 'rooms' ? (My.user.roomOrder ?? []) : (My.user.oneToOneOrder ?? []);

    const sortedRooms = useMemo(() => {
        let list = [...currentRooms];
        if (currentOrder.length > 0) {
            list.sort((a, b) => {
                const aIdx = currentOrder.indexOf(a.id);
                const bIdx = currentOrder.indexOf(b.id);
                if (aIdx === -1 && bIdx === -1) return 0;
                if (aIdx === -1) return 1;
                if (bIdx === -1) return -1;
                return aIdx - bIdx;
            });
        }
        return list;
    }, [currentRooms, currentOrder]);

    function toggleSetting(roomId: string, field: keyof RoomSetting) {
        const current = My.RoomSettings.get(roomId) ?? new RoomSetting(roomId);
        const updated = { ...current, roomId } as RoomSetting;
        (updated as any)[field] = !(current as any)[field];
        My.saveRoomNotificationSetting(roomId, updated);
    }

    // === Reorder logic ===
    function saveOrder(fullOrder: string[]) {
        if (activeTab === 'rooms') {
            My.saveRoomOrder(fullOrder, My.user.oneToOneOrder ?? []);
        } else {
            My.saveRoomOrder(My.user.roomOrder ?? [], fullOrder);
        }
    }

    function reorder(sourceId: string, targetId: string) {
        if (sourceId === targetId) return;
        const fullOrder = sortedRooms.map(r => r.id);
        const sourceIdx = fullOrder.indexOf(sourceId);
        const targetIdx = fullOrder.indexOf(targetId);
        if (sourceIdx === -1 || targetIdx === -1) return;
        fullOrder.splice(sourceIdx, 1);
        fullOrder.splice(targetIdx, 0, sourceId);
        saveOrder(fullOrder);
    }

    // === Mouse drag handlers ===
    const handleDragStart = useCallback((e: React.DragEvent, roomId: string) => {
        dragRef.current = roomId;
        setDragId(roomId);
        e.dataTransfer.effectAllowed = 'move';
        // 기본 drag image 숨기기
        const emptyImg = new Image();
        emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        e.dataTransfer.setDragImage(emptyImg, 0, 0);
        // 커스텀 고스트 생성
        createGhost(e.currentTarget as HTMLElement, e.clientX, e.clientY);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, roomId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverId(roomId);
        // 고스트 상하만 이동
        if (ghostRef.current) {
            ghostRef.current.style.top = `${e.clientY - 20}px`;
        }
    }, []);

    const handleDragEnd = useCallback(() => {
        removeGhost();
        setDragId(null);
        setDragOverId(null);
        dragRef.current = null;
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        const sourceId = dragRef.current;
        if (sourceId) reorder(sourceId, targetId);
        handleDragEnd();
    }, [sortedRooms, activeTab]);

    // === Touch drag handlers ===
    const ghostRef = useRef<HTMLElement | null>(null);

    const createGhost = (element: HTMLElement, x: number, y: number) => {
        const ghost = element.cloneNode(true) as HTMLElement;
        const rect = element.getBoundingClientRect();
        ghost.style.cssText = `
            position: fixed;
            width: ${rect.width}px;
            left: ${rect.left}px;
            top: ${y - 20}px;
            opacity: 0.85;
            z-index: 9999;
            pointer-events: none;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            border-radius: 8px;
            background: white;
            transform: scale(1.02);
        `;
        document.body.appendChild(ghost);
        ghostRef.current = ghost;
    };

    const removeGhost = () => {
        if (ghostRef.current) {
            ghostRef.current.remove();
            ghostRef.current = null;
        }
    };
    const touchTargetRef = useRef<HTMLElement | null>(null);

    const handleTouchStart = useCallback((e: React.TouchEvent, roomId: string) => {
        dragRef.current = roomId;
        touchStartY.current = e.touches[0].clientY;
        touchTargetRef.current = e.currentTarget as HTMLElement;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!dragRef.current || !listRef.current) return;
        const touch = e.touches[0];
        const dy = Math.abs(touch.clientY - touchStartY.current);

        // 10px 이상 이동해야 드래그 모드 진입
        if (!dragId && dy > 10) {
            setDragId(dragRef.current);
            if (touchTargetRef.current) {
                createGhost(touchTargetRef.current, touch.clientX, touch.clientY);
            }
        }

        if (!dragId) return;
        e.preventDefault();

        // 고스트 위치 업데이트 (상하만)
        if (ghostRef.current) {
            ghostRef.current.style.top = `${touch.clientY - 20}px`;
        }

        // 터치 위치의 드롭 타겟 찾기
        const elements = listRef.current.querySelectorAll('[data-room-id]');
        for (const el of elements) {
            const rect = el.getBoundingClientRect();
            if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
                const targetId = el.getAttribute('data-room-id');
                if (targetId && targetId !== dragId) {
                    setDragOverId(targetId);
                }
                break;
            }
        }
    }, [dragId]);

    const handleTouchEnd = useCallback(() => {
        removeGhost();
        if (dragId && dragOverId) {
            reorder(dragId, dragOverId);
        }
        setDragId(null);
        setDragOverId(null);
        dragRef.current = null;
    }, [dragId, dragOverId, sortedRooms, activeTab]);

    const handleTouchCancel = useCallback(() => {
        removeGhost();
        setDragId(null);
        setDragOverId(null);
        dragRef.current = null;
        touchTargetRef.current = null;
    }, []);

    function getSetting(roomId: string) {
        return My.RoomSettings.get(roomId) ?? new RoomSetting(roomId);
    }

    function renderRoom(room: Room) {
        const setting = getSetting(room.id);
        const theOtherUser = activeTab === 'oneToOne' ? Room.getOtherOne2OneUser(room, My.user.id) : null;
        const isDragging = dragId === room.id;
        const isDragOver = dragOverId === room.id && dragId !== room.id;

        return (
            <div
                key={room.id}
                data-room-id={room.id}
                ref={el => { if (el) itemRefs.current.set(room.id, el); }}
                className={cn(
                    isDragging && "bg-primary/10 ring-2 ring-primary/30 rounded-lg",
                    isDragOver && "border-t-2 border-primary",
                )}
                draggable
                onDragStart={(e) => handleDragStart(e, room.id)}
                onDragOver={(e) => handleDragOver(e, room.id)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, room.id)}
                onTouchStart={(e) => handleTouchStart(e, room.id)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
            >
                <div className="flex items-center gap-1 px-1 py-1.5 rounded-lg hover:bg-muted/50 active:bg-muted transition-colors">
                    {/* Drag handle */}
                    <div className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-muted-foreground shrink-0 touch-none">
                        <GripVertical className="w-3.5 h-3.5" />
                    </div>

                    {/* Room name */}
                    <button
                        className="flex-1 min-w-0 text-left text-sm text-[color:rgb(var(--text-body))] truncate hover:text-primary transition-colors"
                        onClick={() => {
                            onNavigateToRoom(room.id);
                            onOpenChange(false);
                        }}
                    >
                        {activeTab === 'rooms' ? `# ${room.name}` : (theOtherUser?.name ?? room.name)}
                    </button>

                    {/* Setting indicators (click to open settings) */}
                    <RoomSettingsPopover roomId={room.id} setting={setting} onToggle={toggleSetting}>
                        <div className="flex items-center gap-0.5 shrink-0 cursor-pointer rounded px-1 py-0.5 hover:bg-muted transition-colors" onClick={e => e.stopPropagation()} onTouchStart={e => e.stopPropagation()}>
                            <span className={cn("p-0.5 rounded", setting.favoriteRoomInMyList ? "bg-yellow-100" : "")}>
                                <Star className={cn("w-3 h-3", setting.favoriteRoomInMyList ? "text-yellow-500 fill-yellow-500" : "text-[color:rgb(var(--text-body))]")} />
                            </span>
                            <span className={cn("p-0.5 rounded", setting.hideRoomInMyList ? "bg-destructive/10" : "")}>
                                <EyeOff className={cn("w-3 h-3", setting.hideRoomInMyList ? "text-destructive" : "text-[color:rgb(var(--text-body))]")} />
                            </span>
                            <span className={cn("p-0.5 rounded", setting.disablePushNotifications ? (setting.allowMentionPushNotifications ? "bg-green-50" : "bg-primary/10") : "")}>
                                {setting.disablePushNotifications && setting.allowMentionPushNotifications
                                    ? <AtSign className="w-3 h-3 text-green-500" />
                                    : <BellOff className={cn("w-3 h-3", setting.disablePushNotifications ? "text-primary" : "text-[color:rgb(var(--text-body))]")} />
                                }
                            </span>
                            <span className={cn("p-0.5 rounded", setting.disableVoiceCallPopups ? "bg-blue-50" : "")}>
                                <PhoneOff className={cn("w-3 h-3", setting.disableVoiceCallPopups ? "text-blue-400" : "text-[color:rgb(var(--text-body))]")} />
                            </span>
                            <span className={cn("p-0.5 rounded", setting.disableEmailReminders ? "bg-purple-50" : "")}>
                                <MailX className={cn("w-3 h-3", setting.disableEmailReminders ? "text-purple-400" : "text-[color:rgb(var(--text-body))]")} />
                            </span>
                        </div>
                    </RoomSettingsPopover>
                </div>
            </div>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(v) => {
            onOpenChange(v);
            if (!v) { setDragId(null); setDragOverId(null); }
        }}>
            <DialogContent className="sm:max-w-md p-0 gap-0 min-h-[50vh] max-h-[80vh] flex flex-col">
                <DialogHeader className="px-4 pt-4 pb-0">
                    <DialogTitle className="text-base"><Trans>Channel Manager</Trans></DialogTitle>
                </DialogHeader>

                {/* Header controls */}
                <div className="px-4 py-3 space-y-2 border-b">
                    {/* Tab switcher */}
                    <div className="flex gap-1 bg-muted p-0.5 rounded-lg">
                        <button
                            className={cn("flex-1 text-xs font-medium py-1.5 rounded-md transition-colors", activeTab === 'rooms' ? "bg-background shadow-sm text-[color:rgb(var(--text-body))]" : "text-muted-foreground hover:text-[color:rgb(var(--text-body))]")}
                            onClick={() => setActiveTab('rooms')}
                        >
                            <Trans>Channels</Trans> ({rooms.length})
                        </button>
                        <button
                            className={cn("flex-1 text-xs font-medium py-1.5 rounded-md transition-colors", activeTab === 'oneToOne' ? "bg-background shadow-sm text-[color:rgb(var(--text-body))]" : "text-muted-foreground hover:text-[color:rgb(var(--text-body))]")}
                            onClick={() => setActiveTab('oneToOne')}
                        >
                            <Trans>1:1</Trans> ({oneToOneRooms.length})
                        </button>
                    </div>
                </div>

                {/* Channel list */}
                <div ref={listRef} className="flex-1 overflow-y-auto p-2 space-y-0.5">
                    {sortedRooms.length === 0 && (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                            <Trans>No channels found</Trans>
                        </div>
                    )}
                    {sortedRooms.map(room => renderRoom(room))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// === Room Settings Popover ===
const RoomSettingsPopover: React.FC<{
    roomId: string;
    setting: RoomSetting;
    onToggle: (roomId: string, field: keyof RoomSetting) => void;
    children: React.ReactNode;
}> = ({ roomId, setting: initialSetting, onToggle, children }) => {
    const [localSetting, setLocalSetting] = useState({ ...initialSetting });
    const pendingRef = useRef<Set<keyof RoomSetting>>(new Set());

    const handleToggle = (field: keyof RoomSetting) => {
        setLocalSetting(prev => ({ ...prev, [field]: !prev[field] } as any));
        pendingRef.current.add(field);
    };

    const handleOpenChange = (open: boolean) => {
        if (!open && pendingRef.current.size > 0) {
            // Popover 닫힐 때 변경된 필드만 한번에 저장
            for (const field of pendingRef.current) {
                onToggle(roomId, field);
            }
            pendingRef.current.clear();
        }
    };

    return (
        <Popover modal={true} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                {children}
            </PopoverTrigger>
            <PopoverContent
                className="w-56 p-2"
                align="end"
                sideOffset={4}
                onTouchStart={e => e.stopPropagation()}
                onTouchMove={e => e.stopPropagation()}
                onTouchEnd={e => e.stopPropagation()}
            >
                <div className="space-y-1">
                    <SettingRow
                        icon={<Star className={cn("w-3.5 h-3.5", localSetting.favoriteRoomInMyList ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground")} />}
                        label={t`Add to favorites`}
                        checked={localSetting.favoriteRoomInMyList}
                        onToggle={() => handleToggle('favoriteRoomInMyList')}
                    />
                    <SettingRow
                        icon={localSetting.hideRoomInMyList ? <EyeOff className="w-3.5 h-3.5 text-destructive" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                        label={t`Hide in My Channels list`}
                        checked={localSetting.hideRoomInMyList}
                        onToggle={() => handleToggle('hideRoomInMyList')}
                    />
                    <SettingRow
                        icon={localSetting.disablePushNotifications ? <BellOff className="w-3.5 h-3.5 text-primary" /> : <Bell className="w-3.5 h-3.5 text-muted-foreground" />}
                        label={t`Disable push notifications`}
                        checked={localSetting.disablePushNotifications}
                        onToggle={() => handleToggle('disablePushNotifications')}
                    />
                    {localSetting.disablePushNotifications && (
                        <div className="ml-4">
                            <SettingRow
                                icon={<AtSign className={cn("w-3.5 h-3.5", localSetting.allowMentionPushNotifications ? "text-green-500" : "text-muted-foreground")} />}
                                label={t`Receive notifications when mentioned`}
                                checked={localSetting.allowMentionPushNotifications}
                                onToggle={() => handleToggle('allowMentionPushNotifications')}
                            />
                        </div>
                    )}
                    <SettingRow
                        icon={localSetting.disableVoiceCallPopups ? <PhoneOff className="w-3.5 h-3.5 text-blue-400" /> : <Phone className="w-3.5 h-3.5 text-muted-foreground" />}
                        label={t`Disable voice call popups`}
                        checked={localSetting.disableVoiceCallPopups}
                        onToggle={() => handleToggle('disableVoiceCallPopups')}
                    />
                    <SettingRow
                        icon={localSetting.disableEmailReminders ? <MailX className="w-3.5 h-3.5 text-purple-400" /> : <Mail className="w-3.5 h-3.5 text-muted-foreground" />}
                        label={t`Disable email reminders`}
                        checked={localSetting.disableEmailReminders}
                        onToggle={() => handleToggle('disableEmailReminders')}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
};

const SettingRow: React.FC<{ icon: React.ReactNode; label: string; checked: boolean; onToggle: () => void }> = ({ icon, label, checked, onToggle }) => (
    <div className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 cursor-pointer" onClick={(e) => { e.stopPropagation(); onToggle(); }} onPointerDown={e => e.stopPropagation()}>
        {icon}
        <span className="text-xs text-card-foreground flex-1">{label}</span>
        <Switch checked={checked} onCheckedChange={onToggle} className="scale-[0.6]" onClick={e => e.stopPropagation()} />
    </div>
);

export default RoomManagerModal;
